import { IEMRSectionSearchResponseData } from "@/api_framework/api_modals/EMRs";
import { SectionSearchDataTypes, useEMRSectionSearchAPI } from "@/app/hooks/api_hooks/emr/useEMRSectionSearchAPI";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Fragment } from "react";
import SimpleBadge from "../../Badges/SimpleBadge";
import { Tooltip } from "react-tooltip";
import { BiRupee } from "react-icons/bi";
import { useLoader } from "@/app/hooks/useLoader";
import CircleSpinnerLoader from "../LoaderComponent/CircleSpinnerLoader";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";

export type EMRSectionSearchResultType = {
  name: string;
  vetic_variant_id?: string | null;
  sku_form?: string | null;
  count?: string;
  item_tag?: string;
  item_mrp?: string;
  is_hd_stock?: boolean;
  prescription_type?: string
};

const EMRSectionSearch: React.FC<{
  searchType: SectionSearchDataTypes;
  flexibleAdd: boolean;
  containerClassName?: string;
  clinicId: string;
  emrType?: string;
  placeholder?: string;
  inputContainerClassName?: string;
  itemActiveClassName?: string;
  onSelectItem?: (item: IEMRSectionSearchResponseData) => void;
  onFlexibleAdd?: (addedItem: string) => void;
  onClick?: () => void;
}> = ({ searchType, containerClassName, inputContainerClassName, onSelectItem, ...props }) => {
  const { searchResults, execute: searchInSection, clearSearchResults } = useEMRSectionSearchAPI();
  const [selected, setSelected] = useState<EMRSectionSearchResultType | undefined>();

  const { getRouteKey } = useRouter();
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { customerId, petId } = routeState.state as ICustomerProfileState;
  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);

  const { execute: logAPI } = useClickLogAPI();

  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");

  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);

  const [query, setQuery] = useState("");

  const callLogAPI = (searchString: string, searchType: string, searchData?: any, clickItem?: any) => {
    const logBody: IClickLogBody = {
      event_type: clickItem ? "EMR_CLICK" : searchData ? "EMR_VIEW" : "EMR_SEARCH",
      scr_id: "EMR",
      client_id: customerId,
      emr_id: emr_id,
      pet_id: petId,
      search_item: searchString,
      search_result: searchData ? searchData : undefined,
      click_on_search_item: clickItem ? clickItem : undefined,
      emr_search_type: searchType,
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      vms_user_id: userLogInfo,
      timestamp: Date.now(),
    };
    logAPI(logBody);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        if (value.length >= 3) {
          searchInSection(value, searchType, props?.clinicId, petId, emr_id, props?.emrType).then((data) => {
            callLogAPI(value, searchType, data);
          });
          callLogAPI(value, searchType);
        }
      }, 800),
    [searchType, props?.clinicId, petId, props?.emrType],
  );

  useEffect(() => {
    debouncedSearch(query);

    // Cleanup to cancel debounce if query changes quickly or component unmounts
    return () => {
      debouncedSearch.cancel();
    };
  }, [query, debouncedSearch]);

  const reducedSearchResults = searchResults?.reduce((final, current) => {
    if (searchType === "Food" || searchType === "Medicines" || searchType === "Deworming") {
      final.push({
        name: current.variant_name ?? current?.item_name ?? null,
        count: current?.total_item_qty,
        vetic_variant_id: current?.vetic_variant_id ?? current?.item_id ?? null,
        sku_form: current?.sku_form ?? current?.form ?? current?.form_name ?? null,
        item_mrp: current?.item_mrp ?? null,
        item_tag: current?.item_tag ?? undefined,
        is_hd_stock: current?.is_hd_stock ?? undefined,
        prescription_type: current?.prescription_type ?? undefined
      });
    } else if (searchType === "Lab") {
      final.push({
        name: current?.item_name ?? null,
        count: current?.total_item_qty ?? null,
        vetic_variant_id: current?.item_id ?? null,
        item_mrp: current?.item_mrp ?? null,
      });
    } else if (searchType === "Treatment") {
      final.push({
        name: current?.item_name ?? null,
        vetic_variant_id: current?.item_id ?? null,
        item_mrp: current?.item_mrp ?? null,
        item_tag: current?.item_tag ?? null,
        count: current?.total_item_qty ?? null,
      });
    } else {
      final.push({
        name: current?.sub_service_name ?? null,
        vetic_variant_id: current?.sub_service_id ?? null,
      });
    }
    return final;
  }, [] as EMRSectionSearchResultType[]);

  const isSearchPending = useLoader("isCustomerEmrSearchInProgress");

  return (
    <div className={clsx(containerClassName)}>
      <Combobox
        value={selected}
        onChange={() => {
          clearSearchResults();
          // setSelected(item);
          // if (onSelectItem) {
          //   console.log("onChange", item);
          //   setQuery("");
          //   onSelectItem(item);
          // }
        }}
      >
        <div className="relative mt-1 w-full">
          <div className="relative border-gray-200 bg-white  border rounded-lg focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 w-full text-left sm:text-sm cursor-default overflow-hidden focus:outline-none">
            <div className="flex justify-start items-center">
              {query?.length >= 3 ? (
                <button
                  className="p-2"
                  onClick={() => {
                    setQuery("");
                    clearSearchResults();
                  }}
                >
                  <XMarkIcon className="w-4 h-4 text-red-500" />
                </button>
              ) : (
                <span className="p-2">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-500" />
                </span>
              )}
              <Combobox.Button className={clsx(inputContainerClassName)}>
                <Combobox.Input
                  autoComplete="off"
                  autoCorrect="off"
                  value={query}
                  placeholder={props?.placeholder ?? "Start Searching ..."}
                  className="py-2 pr-10 pl-3 border-none focus:ring-0 w-full text-gray-900 text-sm leading-5"
                  onChange={(event) => {
                    if (event.target.value.length < 3) {
                      clearSearchResults();
                    }
                    setQuery(event.target.value);
                  }}
                // onClick={(e) => {
                //   console.log("Combobox.Input onClick");
                //   props?.onClick?.();
                //   const inputValue = (e.target as HTMLInputElement).value;
                //   setQuery(inputValue);
                // }}
                />
              </Combobox.Button>
            </div>
            <Combobox.Button className="right-0 absolute inset-y-0 flex items-center pr-2">
              <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Combobox.Options
              className="z-10 absolute bg-white shadow-lg mt-1 py-1 rounded-md ring-1 ring-black/5 w-full max-h-60 overflow-y-auto focus:outline-none sm:text-sm"
              style={{ overflowX: "hidden", scrollbarWidth: "thin" }} // For Firefox
            >
              <div className="pl-4 w-full">
                <span className="text-xs">
                  Search results for <span className="font-semibold text-blue-600">{query}</span>
                </span>
              </div>
              {reducedSearchResults.length === 0 && query !== "" ? (
                <div className="relative px-4 py-2 text-gray-700 cursor-default select-none">
                  {isSearchPending ? <CircleSpinnerLoader h="40" w="40" /> : <p>No Result Found.</p>}
                </div>
              ) : (
                reducedSearchResults.map((result, idx) => (
                  <Combobox.Option
                    key={`${result}:${idx}`}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${active
                        ? props?.itemActiveClassName
                          ? props?.itemActiveClassName
                          : "bg-teal-500 text-white"
                        : "text-gray-900"
                      }`
                    }
                    value={result}
                    onClick={() => {
                      setSelected(result);
                      if (onSelectItem) {
                        clearSearchResults();
                        setQuery("");
                        onSelectItem(result);
                        callLogAPI(query, searchType, null, result);
                      }
                    }}
                  >
                    {({ selected, active }) => (
                      <div className="flex items-center text-[10px] sm:text-xs 2xl:text-sm w-full">
                        <div className="w-[55%] pr-2">
                          <span
                            className="cursor-pointer"
                            data-tooltip-id={`tooltip-${idx}`}
                            data-tooltip-content={result.name}
                          >
                            {result.name}
                          </span>
                          <Tooltip id={`tooltip-${idx}`} style={{ zIndex: 10, fontSize: "10px" }} />
                        </div>
                        <div className="w-[20%] flex justify-end">
                          {result?.item_mrp && (
                            <SimpleBadge
                              colorClassName="text-black ring-transparent"
                              label={
                                <span className="flex items-center">
                                  <BiRupee className="mr-[-2px]" />
                                  {result?.item_mrp}
                                </span>
                              }
                            />
                          )}
                        </div>

                        <div className="w-[25%] flex justify-end items-center gap-2">
                          {result?.is_hd_stock && <p className="text-red-600 text-[13px]">HD</p>}{" "}
                          {result?.count ? (
                            result?.count === "Unavailable" ? (
                              <SimpleBadge
                                colorClassName="text-[#b93b48] bg-[#fbdfe3] ring-[#b93b48] w-[110px] flex items-center justify-center"
                                label={`${result?.count}`}
                              />
                            ) : (
                              <SimpleBadge
                                colorClassName={
                                  result?.is_hd_stock
                                    ? "text-[#006ecf] bg-[#edf5fe] ring-[#006ecf] w-[110px] flex items-center justify-center"
                                    : "text-[#01860A] bg-[#F1FDF4] ring-[#01860A] w-[110px] flex items-center justify-center"
                                }
                                label={`${result?.count}`}
                              />
                            )
                          ) : null}
                        </div>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-white" : "text-teal-600"
                              }`}
                          >
                            <CheckIcon className="w-5 h-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
              {props?.flexibleAdd && query?.length >= 3 && reducedSearchResults.length === 0 && query !== "" && (
                <Combobox.Option
                  value={{ name: query }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (props?.onFlexibleAdd) {
                      props?.onFlexibleAdd(query);
                      setQuery("");
                      clearSearchResults();
                    }
                  }}
                  className="-bottom-1 sticky bg-white p-2 w-full font-semibold text-blue-600 text-center cursor-pointer"
                >
                  <span>+Add</span>
                  <span className="ml-2">{query}</span>
                </Combobox.Option>
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
};

export default EMRSectionSearch;
